import React, { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';

import { useAuth } from '../../contexts/AuthContext';
import logo from '../../logo.jpeg';

const ProtectedLayout: React.FC = () => {
  const { logout, userEmail, isAuthenticated } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [accountDrawerOpen, setAccountDrawerOpen] = useState(false);
  const accountWrapRef = useRef<HTMLDivElement | null>(null);

  const closeMenu = () => {
    setMenuOpen(false);
    setAccountDrawerOpen(false);
  };

  useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      if (!accountDrawerOpen) {
        return;
      }
      if (accountWrapRef.current && !accountWrapRef.current.contains(event.target as Node)) {
        setAccountDrawerOpen(false);
      }
    };

    const onEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setAccountDrawerOpen(false);
      }
    };

    window.addEventListener('mousedown', onPointerDown);
    window.addEventListener('keydown', onEscape);
    return () => {
      window.removeEventListener('mousedown', onPointerDown);
      window.removeEventListener('keydown', onEscape);
    };
  }, [accountDrawerOpen]);

  return (
    <div className="app-shell">
      <header className="top-nav">
        <NavLink
          to="/"
          className="brand-wrap brand-link"
          onClick={closeMenu}
          aria-label="Go to EcoPredict homepage"
        >
          <span className="brand-emblem" aria-hidden="true">
            <img src={logo} alt="" className="brand-logo" />
          </span>
          <span className="brand-copy">
            <span className="brand-text">EcoPredict</span>
            <span className="brand-subtext">THE HEALTHY BIODIVERSITY</span>
          </span>
        </NavLink>

        <button
          type="button"
          className={`menu-toggle ${menuOpen ? 'open' : ''}`}
          aria-label={menuOpen ? 'Close navigation menu' : 'Toggle navigation menu'}
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((prev) => !prev)}
        >
          <span />
          <span />
          <span />
        </button>

        <nav className={`main-nav desktop-nav`} aria-label="Main navigation">
          <NavLink to="/dashboard" className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')} onClick={closeMenu}>
            Dashboard
          </NavLink>
          <NavLink to="/search" className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')} onClick={closeMenu}>
            Search
          </NavLink>
          <NavLink to="/risk-map" className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')} onClick={closeMenu}>
            Risk Map
          </NavLink>
          <NavLink to="/analytics" className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')} onClick={closeMenu}>
            Analytics
          </NavLink>
          <NavLink to="/about" className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')} onClick={closeMenu}>
            About
          </NavLink>
        </nav>

        <div ref={accountWrapRef} className={`account-wrap ${menuOpen ? 'open' : ''} ${accountDrawerOpen ? 'drawer-open' : ''}`}>
          {isAuthenticated ? (
            <div className="account-drawer-anchor">
              <button
                type="button"
                className="account-trigger"
                aria-expanded={accountDrawerOpen}
                aria-haspopup="menu"
                onClick={() => setAccountDrawerOpen((prev) => !prev)}
              >
                <span className="account-name">{userEmail || 'account'}</span>
                <span className="account-chevron" aria-hidden="true">▾</span>
              </button>

              {accountDrawerOpen && (
                <div className="account-drawer" role="menu" aria-label="Account menu">
                  <NavLink
                    to="/profile"
                    className="account-drawer-link"
                    role="menuitem"
                    onClick={() => setAccountDrawerOpen(false)}
                  >
                    Profile
                  </NavLink>
                  <NavLink
                    to="/settings"
                    className="account-drawer-link"
                    role="menuitem"
                    onClick={() => setAccountDrawerOpen(false)}
                  >
                    Settings
                  </NavLink>
                  <button
                    type="button"
                    className="account-drawer-logout"
                    role="menuitem"
                    onClick={() => {
                      setAccountDrawerOpen(false);
                      closeMenu();
                      logout();
                    }}
                  >
                    Logout
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="auth-actions">
              <NavLink to="/login" className="auth-link-btn auth-link-btn-ghost" onClick={closeMenu}>
                Sign in
              </NavLink>
              <NavLink to="/signup" className="auth-link-btn" onClick={closeMenu}>
                Sign up
              </NavLink>
            </div>
          )}
        </div>
      </header>

      <div className={`mobile-drawer ${menuOpen ? 'open' : ''}`} aria-hidden={!menuOpen}>
        <div className="mobile-drawer-panel" role="dialog" aria-modal="true" aria-label="Navigation menu">
          <div className="mobile-drawer-header">
            <div>
              <p className="mobile-drawer-kicker">Navigate</p>
              <h2>EcoPredict Menu</h2>
            </div>

            <button type="button" className="mobile-drawer-close" aria-label="Close menu" onClick={closeMenu}>
              ×
            </button>
          </div>

          <nav className="mobile-drawer-nav" aria-label="Mobile navigation">
            <NavLink to="/dashboard" className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')} onClick={closeMenu}>
              Dashboard
            </NavLink>
            <NavLink to="/search" className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')} onClick={closeMenu}>
              Search
            </NavLink>
            <NavLink to="/risk-map" className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')} onClick={closeMenu}>
              Risk Map
            </NavLink>
            <NavLink to="/analytics" className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')} onClick={closeMenu}>
              Analytics
            </NavLink>
            <NavLink to="/settings" className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')} onClick={closeMenu}>
              Settings
            </NavLink>
            <NavLink to="/about" className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')} onClick={closeMenu}>
              About
            </NavLink>
          </nav>

          <div className="mobile-drawer-footer">
            {isAuthenticated ? (
              <>
                <span className="account-name">{userEmail || 'account'}</span>
                <button
                  className="logout-btn"
                  onClick={() => {
                    closeMenu();
                    logout();
                  }}
                >
                  Logout
                </button>
              </>
            ) : (
              <div className="auth-actions mobile-auth-actions">
                <NavLink to="/login" className="auth-link-btn auth-link-btn-ghost" onClick={closeMenu}>
                  Sign in
                </NavLink>
                <NavLink to="/signup" className="auth-link-btn" onClick={closeMenu}>
                  Sign up
                </NavLink>
              </div>
            )}
          </div>
        </div>
      </div>

      {menuOpen && <button type="button" className="menu-backdrop" aria-label="Close menu" onClick={closeMenu} />}

      <main className="page-content">
        <Outlet />
      </main>
    </div>
  );
};

export default ProtectedLayout;
